// PRE RMA — Pré-inventaire / Inventaire (branches indépendantes du RMA) + liens avec la gestion des intrants RMA.
// Source : PREINVENTAIRE_INVENTAIRE_V5_VIERGE.html. Toutes les règles sont ici (écran, API, exports, tests = mêmes fonctions).
//
// LIENS DE DONNÉES (décisions du client, oct. 2026) :
//  1. Intrants RMA → pré-inventaire : toute sortie saisie dans la gestion des intrants RMA (utilisée + redéployée + perdue +
//     sortie AC) est DÉDUITE du stock pré-inventaire : le stock affiché = stock compté (détail + gros) s'il a été compté,
//     sinon stock théorique RMA (début + reçue − sorties).
//  2. Pré-inventaire → intrants RMA : le stock compté (détail + gros, hors FANOME) alimente le « stock de fin » (SDU) RMA.
//     Le champ reste modifiable : une valeur saisie dans le RMA l'emporte.
//  3. Liste des médicaments (inventaire) → intrants RMA « Médicaments essentiels » : les médicaments marqués « traceur »
//     deviennent des lignes du RMA, alimentées par l'inventaire (début, entrée, dispensation, périmés, fin).
//  4. Finance : Σ des montants de dispensation + périmés/avariés de l'inventaire = « Sorties du mois » (Médicaments) du T19.

import { STOCK_PROGRAMMES, sorties, stockFin } from './stock.ts';
import type { LigneStock } from './stock.ts';

export interface MedicamentCatalogue { id: string; nom: string; pu: number; traceur: boolean }

export interface LigneInventaire {
  /** Prix unitaire « figé » à la première saisie du mois (un changement de PU ne modifie pas les mois passés). */
  pu: number | null;
  debut: number | null; entree: number | null; perime: number | null;
  detail: number | null; gros: number | null;
  exp: string; obs: string;
}
export const CHAMPS_INVENTAIRE_NUM = ['pu', 'debut', 'entree', 'perime', 'detail', 'gros'] as const;
export const ligneInventaireVide = (): LigneInventaire => ({ pu: null, debut: null, entree: null, perime: null, detail: null, gros: null, exp: '', obs: '' });

export interface PreLigne { detail: number | null; gros: number | null; obs: string }
export const preLigneVide = (): PreLigne => ({ detail: null, gros: null, obs: '' });

/** Programmes du pré-inventaire autres que FANOME : mêmes produits (et mêmes clés) que la gestion des intrants RMA. */
export const PRE_PROGRAMMES = STOCK_PROGRAMMES.filter((p) => p.code !== 'ME');
export const PRE_PROGRAMME_FANOME = { code: 'FANOME', nom: 'FANOME', couleur: '#37474f' };

const num = (x: unknown) => { const v = typeof x === 'number' ? x : parseFloat(String(x ?? '')); return Number.isFinite(v) && v > 0 ? v : 0; };
const arrondi2 = (x: number) => Math.round(x * 100) / 100;
export const arrondiAriary = (x: number) => Math.round(x);

// ------------------------------------------------------------------ Inventaire (FANOME)
export const finInv = (l: Partial<LigneInventaire>) => arrondi2(num(l.detail) + num(l.gros));
export const estCompte = (l: Partial<LigneInventaire>) => (l.detail !== null && l.detail !== undefined) || (l.gros !== null && l.gros !== undefined);

/** Stock début : fin du mois précédent (automatique, verrouillé) dès que l'inventaire existe le mois d'avant ; sinon saisie manuelle. */
export function debutInv(l: Partial<LigneInventaire>, finPrec: number | null | undefined): { valeur: number; reporte: boolean } {
  if (finPrec !== null && finPrec !== undefined) return { valeur: finPrec, reporte: true };
  return { valeur: num(l.debut), reporte: false };
}

export interface CalculInventaire {
  pu: number; debut: number; reporte: boolean; entree: number; perime: number; fin: number; compte: boolean;
  /** Dispensation = début + entrée − fin − périmé (null si l'article n'est pas compté). */
  dispensation: number | null;
  valeurDebut: number; valeurEntree: number; valeurDispensation: number; valeurPerime: number; valeurFin: number;
}

export function calculerLigneInventaire(l: Partial<LigneInventaire>, puCatalogue: number, finPrec: number | null | undefined): CalculInventaire {
  const pu = l.pu !== null && l.pu !== undefined ? num(l.pu) : num(puCatalogue);
  const { valeur: debut, reporte } = debutInv(l, finPrec);
  const compte = estCompte(l);
  const fin = compte ? finInv(l) : 0;
  const entree = num(l.entree), perime = num(l.perime);
  const dispensation = compte ? arrondi2(debut + entree - fin - perime) : null;
  return {
    pu, debut, reporte, entree, perime, fin, compte, dispensation,
    valeurDebut: debut * pu, valeurEntree: entree * pu,
    valeurDispensation: dispensation === null ? 0 : dispensation * pu,
    valeurPerime: perime * pu, valeurFin: fin * pu,
  };
}

export interface TotauxInventaire {
  nbArticles: number; nbComptes: number;
  valeurDebut: number; valeurEntree: number; valeurDispensation: number; valeurPerime: number; valeurFin: number;
  /** Recette totale = Σ dispensation × PU. */
  recetteTotale: number;
  depense: number;
  /** Montant à verser = recette totale − dépense (frais de transport des intrants). */
  montantAVerser: number;
  /** Sorties du mois côté finance : dispensation + périmés/avariés. */
  sortiesValeur: number;
}

export function totauxInventaire(calculs: CalculInventaire[], depense: number | null | undefined): TotauxInventaire {
  const t = { nbArticles: calculs.length, nbComptes: 0, valeurDebut: 0, valeurEntree: 0, valeurDispensation: 0, valeurPerime: 0, valeurFin: 0 };
  for (const c of calculs) {
    if (c.compte) t.nbComptes += 1;
    t.valeurDebut += c.valeurDebut; t.valeurEntree += c.valeurEntree; t.valeurDispensation += c.valeurDispensation;
    t.valeurPerime += c.valeurPerime; t.valeurFin += c.valeurFin;
  }
  const dep = num(depense);
  return {
    ...t, recetteTotale: t.valeurDispensation, depense: dep, montantAVerser: t.valeurDispensation - dep,
    sortiesValeur: t.valeurDispensation + t.valeurPerime,
  };
}

export interface ControleInventaire { niveau: 'warn'; message: string }
export function controlerLigneInventaire(_l: Partial<LigneInventaire>, c: CalculInventaire): ControleInventaire | null {
  if (c.dispensation !== null && c.dispensation < 0) return { niveau: 'warn', message: `Dispensation négative (${c.dispensation}) : le stock compté dépasse début + entrée − périmé.` };
  if (c.perime > c.debut + c.entree) return { niveau: 'warn', message: 'Périmés supérieurs à début + entrée.' };
  return null;
}

export type StatutPeremption = 'perime' | 'ce_mois' | 'proche' | null;
/** Péremption AAAA-MM comparée au mois de travail : périmé (mois passé), ce mois, proche (≤ 3 mois). */
export function statutPeremption(exp: string | null | undefined, annee: number, mois: number): StatutPeremption {
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(exp ?? '');
  if (!m) return null;
  const ecart = (+m[1] - annee) * 12 + (+m[2] - mois);
  if (ecart < 0) return 'perime';
  if (ecart === 0) return 'ce_mois';
  return ecart <= 3 ? 'proche' : null;
}

export function validerLigneInventaire(l: Partial<LigneInventaire>): { ok: boolean; erreurs: string[] } {
  const erreurs: string[] = [];
  for (const k of CHAMPS_INVENTAIRE_NUM) {
    const v = l[k];
    if (v === null || v === undefined) continue;
    if (typeof v !== 'number' || !Number.isFinite(v) || v < 0 || v > 999_999_999) erreurs.push(`${k} : nombre positif attendu.`);
    else if (Math.round(v * 100) !== v * 100 && Math.abs(Math.round(v * 100) - v * 100) > 1e-6) erreurs.push(`${k} : 2 décimales maximum.`);
  }
  if (l.exp && !/^\d{4}-(0[1-9]|1[0-2])$/.test(l.exp)) erreurs.push('Péremption : format AAAA-MM attendu.');
  if (typeof l.obs === 'string' && l.obs.length > 300) erreurs.push('Observation : 300 caractères maximum.');
  const extra = Object.keys(l).filter((k) => ![...CHAMPS_INVENTAIRE_NUM, 'exp', 'obs'].includes(k));
  if (extra.length) erreurs.push(`Champ inconnu : ${extra.join(', ')}.`);
  return { ok: erreurs.length === 0, erreurs };
}

export function validerMoisInventaire(d: { depense?: number | null }): { ok: boolean; erreurs: string[] } {
  const v = d?.depense;
  if (v === null || v === undefined) return { ok: true, erreurs: [] };
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 999_999_999 ? { ok: true, erreurs: [] } : { ok: false, erreurs: ['Dépense : nombre positif attendu.'] };
}

export function validerMedicament(m: Partial<MedicamentCatalogue>): { ok: boolean; erreurs: string[] } {
  const erreurs: string[] = [];
  if (typeof m.id !== 'string' || !/^[A-Za-z0-9_-]{1,40}$/.test(m.id)) erreurs.push('Identifiant invalide.');
  if (typeof m.nom !== 'string' || m.nom.trim().length < 2 || m.nom.length > 120) erreurs.push('Désignation : 2 à 120 caractères.');
  if (typeof m.pu !== 'number' || !Number.isFinite(m.pu) || m.pu < 0 || m.pu > 99_999_999) erreurs.push('PU : nombre positif attendu.');
  if (typeof m.traceur !== 'boolean') erreurs.push('Traceur : booléen attendu.');
  return { ok: erreurs.length === 0, erreurs };
}

// ------------------------------------------------------------------ Pré-inventaire
export const comptePre = (p: Partial<PreLigne> | undefined): number | null =>
  p && ((p.detail !== null && p.detail !== undefined) || (p.gros !== null && p.gros !== undefined)) ? arrondi2(num(p.detail) + num(p.gros)) : null;

export interface EtatPreInventaire {
  /** Sorties saisies dans la gestion des intrants RMA (déduites du stock pré-inventaire). */
  sortie: number;
  /** Stock théorique = début + reçue − sorties RMA. */
  theorique: number;
  /** Stock compté (détail + gros) ou null si pas encore compté. */
  compte: number | null;
  /** Stock pré-inventaire affiché : compté s'il existe, sinon théorique (sorties RMA déduites). */
  stock: number;
  /** Compté − théorique (null si pas compté). */
  ecart: number | null;
}

export function etatPreInventaire(stock: LigneStock | undefined, debutEff: number, pre: Partial<PreLigne> | undefined): EtatPreInventaire {
  const s = stock ?? { debut: null, recue: null, utilisee: null, redeployee: null, perdue: null, sortieAc: null, sdu: null, joursRupture: null };
  const theorique = stockFin(s, debutEff);
  const compte = comptePre(pre);
  return { sortie: sorties(s), theorique, compte, stock: compte ?? theorique, ecart: compte === null ? null : arrondi2(compte - theorique) };
}

/** Stock de fin (SDU) effectif côté RMA : valeur saisie (modifiable) sinon stock compté au pré-inventaire. */
export function sduEffectif(sduSaisi: number | null | undefined, comptePreInv: number | null | undefined): number | null {
  if (sduSaisi !== null && sduSaisi !== undefined) return sduSaisi;
  if (comptePreInv !== null && comptePreInv !== undefined) return Math.floor(comptePreInv);
  return null;
}
export const sduVientDuPreInventaire = (sduSaisi: number | null | undefined, comptePreInv: number | null | undefined) =>
  (sduSaisi === null || sduSaisi === undefined) && comptePreInv !== null && comptePreInv !== undefined;

export function validerPreLigne(p: Partial<PreLigne>): { ok: boolean; erreurs: string[] } {
  const erreurs: string[] = [];
  for (const k of ['detail', 'gros'] as const) {
    const v = p[k];
    if (v !== null && v !== undefined && (typeof v !== 'number' || !Number.isFinite(v) || v < 0 || v > 999_999_999)) erreurs.push(`${k} : nombre positif attendu.`);
  }
  if (typeof p.obs === 'string' && p.obs.length > 300) erreurs.push('Observation : 300 caractères maximum.');
  const extra = Object.keys(p).filter((k) => !['detail', 'gros', 'obs'].includes(k));
  if (extra.length) erreurs.push(`Champ inconnu : ${extra.join(', ')}.`);
  return { ok: erreurs.length === 0, erreurs };
}

// ------------------------------------------------------------------ Médicaments traceurs → intrants RMA « ME »
export const cleTraceur = (idMedicament: string) => `ME:i:${idMedicament}`;
/**
 * Produits « Médicaments essentiels » du catalogue RMA qui correspondent à un médicament de l'inventaire (liste du fichier
 * d'origine). Quand TOUS les médicaments liés sont marqués traceurs, la ligne manuelle est remplacée par la ligne
 * alimentée par l'inventaire ; si le client retire un traceur, la ligne manuelle d'origine réapparaît.
 */
export const ME_LIEN_DEFAUT: Record<number, string[]> = {
  1: ['i5'], 2: ['i6'], 3: ['i7'], 5: ['i49'], 7: ['i23'], 10: ['i33'], 12: ['i35'], 13: ['i37'], 14: ['i39'], 16: ['i46'], 17: ['i51'], 18: ['i53', 'i55'],
};
export function indexMeManuels(catalogue: MedicamentCatalogue[]): number[] {
  const traceurs = new Set(catalogue.filter((m) => m.traceur).map((m) => m.id));
  const total = STOCK_PROGRAMMES.find((p) => p.code === 'ME')?.produits.length ?? 0;
  const out: number[] = [];
  for (let i = 0; i < total; i++) {
    const liens = ME_LIEN_DEFAUT[i];
    if (!liens || !liens.every((id) => traceurs.has(id))) out.push(i);
  }
  return out;
}
export const estCleTraceur = (cle: string) => /^ME:i:[A-Za-z0-9_-]{1,40}$/.test(cle);

/**
 * Ligne RMA d'un médicament traceur, entièrement alimentée par l'inventaire (saisie unique) :
 * début, reçue (= entrée), perdue (= périmés/avariés), SDU (= stock fin). Utilisée = dispensation − redéployée − sortie AC,
 * de sorte que Restant fin RMA = stock fin de l'inventaire. Seuls redéployée, sortie AC et jours de rupture restent à saisir.
 */
export function ligneStockDepuisInventaire(c: CalculInventaire, manuel: Partial<LigneStock> | undefined): LigneStock {
  const redep = num(manuel?.redeployee), ac = num(manuel?.sortieAc);
  return {
    debut: c.debut, recue: c.entree, perdue: c.perime,
    utilisee: c.dispensation === null ? null : Math.max(0, Math.round(c.dispensation - redep - ac)),
    redeployee: manuel?.redeployee ?? null, sortieAc: manuel?.sortieAc ?? null,
    sdu: c.compte ? Math.floor(c.fin) : null, joursRupture: manuel?.joursRupture ?? null,
  };
}

// ------------------------------------------------------------------ Lien finance
export interface ControleLienFinance { niveau: 'ok' | 'warn'; ecart: number; attendu: number; message: string }
/** Total « Sorties du mois » du T19 (médicaments) doit être égal à dispensation + périmés/avariés de l'inventaire. */
export function controlerLienFinanceInventaire(sortiesFinance: number, t: TotauxInventaire): ControleLienFinance | null {
  if (t.nbComptes === 0) return null;
  const attendu = arrondiAriary(t.sortiesValeur), ecart = arrondiAriary(sortiesFinance) - attendu;
  if (Math.abs(ecart) <= 1) return { niveau: 'ok', ecart: 0, attendu, message: 'Sorties = dispensation + périmés de l\'inventaire.' };
  return { niveau: 'warn', ecart, attendu, message: `Écart de ${ecart.toLocaleString('fr-FR')} Ar avec l'inventaire (dispensation + périmés = ${attendu.toLocaleString('fr-FR')} Ar).` };
}
/** Valeurs proposées au T19 (section I) à partir de l'inventaire — appliquées uniquement sur action de l'utilisateur. */
export const suggestionFinanceDepuisInventaire = (t: TotauxInventaire) =>
  ({ s1: arrondiAriary(t.valeurDispensation), s3: arrondiAriary(t.valeurPerime), ph: arrondiAriary(t.valeurFin) });

// ------------------------------------------------------------------ Catalogue par défaut (liste du fichier d'origine)
export const MEDICAMENTS_DEFAUT: MedicamentCatalogue[] = [
  { id: 'i1', nom: "Acide Ascorbique 500mg cp", pu: 135, traceur: false },
  { id: 'i2', nom: "Alcool éthylique 70°Fl", pu: 12404, traceur: false },
  { id: 'i3', nom: "Aluminium Hydroxyde", pu: 57, traceur: false },
  { id: 'i4', nom: "Amoxicilline 250mg gl", pu: 102, traceur: false },
  { id: 'i5', nom: "Amoxicilline 250mg/5ml buv", pu: 2970, traceur: true },
  { id: 'i6', nom: "Amoxicilline 500 mg gel", pu: 170, traceur: true },
  { id: 'i7', nom: "Amoxicilline acide clavulanique", pu: 743, traceur: true },
  { id: 'i8', nom: "Ampiicilline 500 mg gel", pu: 210, traceur: false },
  { id: 'i9', nom: "Ampiicilline fl 1g", pu: 878, traceur: false },
  { id: 'i10', nom: "Atropine Amp 1mg/ml", pu: 816, traceur: false },
  { id: 'i11', nom: "Benzoique et salicylique AC 6% 3%pd", pu: 4185, traceur: false },
  { id: 'i12', nom: "Butyscopolamine 10mg cp", pu: 607, traceur: false },
  { id: 'i13', nom: "Butyscopolamine 20mg/ml Inj amp", pu: 1485, traceur: false },
  { id: 'i14', nom: "Calcium lactate 300 mg cp", pu: 68, traceur: false },
  { id: 'i15', nom: "Calcuim gluconate amp inj", pu: 1620, traceur: false },
  { id: 'i16', nom: "Carbocysteine 5% adulte fl 100 l", pu: 4725, traceur: false },
  { id: 'i17', nom: "Carbocysteine 2% fl", pu: 3267, traceur: false },
  { id: 'i18', nom: "Charbon actif cp", pu: 211, traceur: false },
  { id: 'i19', nom: "Chlorpheniramine 4mg cp", pu: 32, traceur: false },
  { id: 'i20', nom: "Cimetidine cp 200mg", pu: 158, traceur: false },
  { id: 'i21', nom: "Ciprofloxacine 500mg cp", pu: 183, traceur: false },
  { id: 'i22', nom: "Complexe vitaminique B inj Amp 2ml", pu: 297, traceur: false },
  { id: 'i23', nom: "Cotrimoxazole 480mg cp", pu: 73, traceur: true },
  { id: 'i24', nom: "Cotrimoxazole 240mg/5ml sirop", pu: 2363, traceur: false },
  { id: 'i25', nom: "Dexamethasone 4mg amp", pu: 230, traceur: false },
  { id: 'i26', nom: "Diazepam cp", pu: 88, traceur: false },
  { id: 'i27', nom: "Diazepam 5mg/ml amp", pu: 998, traceur: false },
  { id: 'i28', nom: "Diclofenac Amp 75 mg", pu: 258, traceur: false },
  { id: 'i29', nom: "EPPI 5ml", pu: 183, traceur: false },
  { id: 'i30', nom: "Erythromicine 125 mg cp", pu: 256, traceur: false },
  { id: 'i31', nom: "Furosemide Amp inj.", pu: 300, traceur: false },
  { id: 'i32', nom: "Furosemide cp 40 mg", pu: 54, traceur: false },
  { id: 'i33', nom: "Genta 80mg amp", pu: 252, traceur: true },
  { id: 'i34', nom: "gentamycine collyre 0,3%", pu: 1890, traceur: false },
  { id: 'i35', nom: "Ibuprofene cp", pu: 52, traceur: true },
  { id: 'i36', nom: "Methyldopa 250 mg cp", pu: 270, traceur: false },
  { id: 'i37', nom: "Metoclopramide cp 10 mg", pu: 54, traceur: true },
  { id: 'i38', nom: "Metoclopramide inj Amp", pu: 486, traceur: false },
  { id: 'i39', nom: "Metronidazole cp", pu: 46, traceur: true },
  { id: 'i40', nom: "Metronidazole 500mg/100 ml inj", pu: 2430, traceur: false },
  { id: 'i41', nom: "Multivitamine cp", pu: 37, traceur: false },
  { id: 'i42', nom: "Nifedipine 10 mg cp", pu: 108, traceur: false },
  { id: 'i43', nom: "Nystatine cp gyneco", pu: 550, traceur: false },
  { id: 'i44', nom: "Omeprazole 20 mg gel", pu: 95, traceur: false },
  { id: 'i45', nom: "Oxytocine 10 UI inj", pu: 638, traceur: false },
  { id: 'i46', nom: "Paracetamole 500mg cp", pu: 65, traceur: true },
  { id: 'i47', nom: "Paracétamol 100 mg suppo", pu: 638, traceur: false },
  { id: 'i48', nom: "Paracétamole 125 mg suppo", pu: 405, traceur: false },
  { id: 'i49', nom: "Penicilline G 1M fl", pu: 615, traceur: true },
  { id: 'i50', nom: "Penicilline bezanthine 2,4M fl", pu: 1566, traceur: false },
  { id: 'i51', nom: "Phenobarbital 50mg cp", pu: 100, traceur: true },
  { id: 'i52', nom: "Prednisolone 5 mg cp", pu: 57, traceur: false },
  { id: 'i53', nom: "SRO Sachet", pu: 658, traceur: true },
  { id: 'i54', nom: "Vitamine K1 amp", pu: 1572, traceur: false },
  { id: 'i55', nom: "Zinc cp", pu: 95, traceur: true },
  { id: 'i56', nom: "Bande de gaz non sterile 10cm× 4m", pu: 675, traceur: false },
  { id: 'i57', nom: "Coton hydropphile", pu: 1654, traceur: false },
  { id: 'i58', nom: "Compresse hydrophile 10cm×10cm", pu: 81, traceur: false },
  { id: 'i59', nom: "Epicranien 21GUU", pu: 270, traceur: false },
  { id: 'i60', nom: "Perfuseur steril", pu: 1121, traceur: false },
  { id: 'i61', nom: "Polyvidone iodée 10% dermique jaune", pu: 9780, traceur: false },
  { id: 'i62', nom: "Seringue 10ml", pu: 297, traceur: false },
  { id: 'i63', nom: "Seringue 5ml", pu: 243, traceur: false },
  { id: 'i64', nom: "fil non résorbables", pu: 17590, traceur: false },
  { id: 'i65', nom: "Catheter", pu: 945, traceur: false },
  { id: 'i66', nom: "Sparadrap ( en cm)", pu: 53, traceur: false },
];
